/**
 * Qué indicador de actividad mostrar durante un turno en streaming.
 *
 * ── El problema que resuelve ──────────────────────────────────────────────
 *
 * Medido en noprod el 2026-09-07 (hilo `fcb185dc`, ejecución `1174873`): un turno
 * de 76 s con 8 llamadas a herramientas en 4 vueltas del bucle del agente. Los
 * eventos NO llegan repartidos: llegan en ráfagas al cerrar cada vuelta, con
 * huecos de 19 s, 37 s y 11 s en los que no llega absolutamente nada.
 *
 * La causa está en el fork (`streamCallback.ts`): los `tool_call_chunks` hacen
 * `return` sin emitir, y el `tool-call-start` solo sale en `flushFinal`. Mientras
 * el modelo escribe los argumentos de la herramienta —2.838 y 2.972 caracteres de
 * JSON en ese turno— no hay ni un evento que mandar. La vuelta 2 tardó 35,7 s en
 * producir 125 caracteres de texto.
 *
 * Durante esos huecos el usuario veía el texto ya escrito y un cursor, y nada más.
 *
 * ── Por qué hace falta un reloj ───────────────────────────────────────────
 *
 * En el hueco no cambia NINGÚN campo del buffer. Cualquier condición derivada solo
 * de `text` / `roundDone` / `toolCalls` / `agentStatus` vale exactamente igual en el
 * token 1 que en el segundo 30, así que una regla que encienda el indicador en el
 * hueco lo encendería también mientras el modelo escribe, compitiendo con el cursor.
 * La única variable que distingue los dos casos es el tiempo transcurrido desde el
 * último cambio, y eso lo aporta `useStreamSilence`.
 *
 * ── Por qué el modo importa, y no solo el booleano ────────────────────────
 *
 * Durante el silencio, `buffer.toolCalls` contiene las herramientas de la vuelta
 * ANTERIOR: el reducer las conserva en el round-promote (`streamingBufferReducer.js`,
 * caso DELTA con `state.roundDone`) y solo las reemplaza en el siguiente
 * `TOOL_CALL_START`. Pintar esa etiqueta en el hueco diría «Creando agente» cuando
 * esa herramienta ya terminó y el modelo está redactando los argumentos de otra
 * distinta. Es exactamente la clase de afirmación comprobable-y-falsa que prohíbe
 * #911. En el silencio, por tanto, frase neutra: lo único honesto que se puede decir
 * es «sigo aquí», y el cronómetro lo respalda.
 *
 * Vive aquí, junto a `scrollPolicy.js`, por la misma razón que aquel: el chat
 * principal y el chat modular ya divergieron una vez en decisiones de este tipo —
 * el indicador duplicado del Builder y el scroll que no seguía salieron los dos de
 * tener dos copias de la misma regla.
 */

/**
 * Cuánto silencio hace falta para dar por hecho que el motor está pensando.
 *
 * El fork agrupa deltas cada 60 ms (`ChatAwsBedrockAdvancedStreaming.ts`) y el buffer
 * del backend puede retener uno hasta 1.200 ms esperando al hueco de secuencia
 * (`GAP_TOLERANCE_MS` en `streamingBufferService.js`). 2.000 ms queda por encima del
 * peor retraso que el propio transporte puede introducir —o sea, no se enciende por
 * un hipo de red— y muy por debajo de los huecos reales medidos, de 11 a 37 s.
 */
export const SILENCIO_MS = 2000;

/**
 * @typedef {'herramienta'|'silencio'|'oculto'} ModoActividad
 *   - `herramienta`: hay una herramienta en curso de verdad; se pinta su etiqueta.
 *   - `silencio`: no llega nada desde hace rato; frase neutra + cronómetro.
 *   - `oculto`: el texto está fluyendo (o el turno se está deteniendo); manda el cursor.
 */

/**
 * Decide qué mostrar al pie de la burbuja en streaming.
 *
 * Las dos primeras cláusulas son las que ya estaban en `ChatMessages.jsx` antes de
 * este cambio, verbatim; están en el banco de tests como regresión.
 *
 * @param {object}   params
 * @param {string}   [params.texto]        `buffer.text` — texto de la ronda en curso.
 * @param {boolean}  [params.rondaCerrada] `buffer.roundDone` — llegó el delta `done:true`.
 * @param {boolean}  [params.hayHerramientas] `buffer.toolCalls.length > 0`.
 * @param {boolean}  [params.hayEstado]    Hay `currentTask` REAL del canal lateral
 *   `agent_status_updated`. Nunca la frase de relleno: si se le pasara el relleno,
 *   `rondaCerrada && hayEstado` sería siempre cierta y la regla perdería sentido.
 * @param {boolean}  [params.silencioso]   Sin novedades desde hace `SILENCIO_MS`.
 * @param {boolean}  [params.deteniendo]   El usuario pulsó ⏹ y aún no llegó la confirmación.
 * @returns {{ visible: boolean, modo: ModoActividad }}
 */
export function decidirActividad({
  texto = '',
  rondaCerrada = false,
  hayHerramientas = false,
  hayEstado = false,
  silencioso = false,
  deteniendo = false,
} = {}) {
  // El ⏹ apaga todo. Entre la pulsación y la confirmación del backend siguen
  // llegando (o dejando de llegar) eventos del turno que se está cancelando, y
  // decir «trabajando» justo cuando el usuario cree haber parado es peor que no
  // decir nada. El silencio, además, hace este caso mucho más probable.
  if (deteniendo) return { visible: false, modo: 'oculto' };

  // Herramienta en marcha sin texto todavía: el caso limpio, la etiqueta es verdad.
  if (hayHerramientas && !texto) return { visible: true, modo: 'herramienta' };

  // La ronda cerró (`done:true`) y algo sigue ejecutándose: una recuperación RAG de
  // 5-30 s deja la burbuja con aspecto de congelada si no se pinta nada aquí.
  if (rondaCerrada && (hayHerramientas || hayEstado)) {
    return { visible: true, modo: 'herramienta' };
  }

  // Llegar aquí con la ronda cerrada significa que el stream del modelo terminó y no
  // se anunció ni herramienta ni estado: lo más probable es que el turno esté acabando
  // y falte solo que el `agent-finish` limpie la burbuja. No se enciende nada.
  //
  // Es una supresión defensiva, y el número que la justifica: el cierre puede llegar
  // diferido hasta 1,2 s (`scheduleLifecycle` espera al delta que le adelantó — ver
  // `agentEventController._handleToolCallStart`), así que contra un umbral de 2.000 ms
  // el margen es de 700 ms. Sin esta cláusula, un turno cuyo cierre se acercara a ese
  // límite pintaría «Pensando…» durante el último instante ANTES de sustituir la
  // burbuja por el mensaje definitivo — un parpadeo en cada turno, no un caso raro.
  //
  // No cuesta ninguno de los huecos que este módulo existe para cubrir: en los tres
  // medidos (19 s, 37 s, 11 s) el modelo estaba escribiendo argumentos, o sea que el
  // stream seguía abierto y `rondaCerrada` era false.
  if (rondaCerrada) return { visible: false, modo: 'oculto' };

  // Nada llega desde hace rato y la ronda sigue abierta: el modelo está pensando o
  // escribiendo argumentos. Frase neutra, nunca la herramienta obsoleta.
  if (silencioso) return { visible: true, modo: 'silencio' };

  return { visible: false, modo: 'oculto' };
}
